import React, { useState } from 'react';
import { formatUSD, formatDate } from '../utils/keyGenerator';
import { downloadStatementPDF, downloadSlipPDF } from '../utils/pdfUtils';

export default function Ledger({ transactions, setSlip }) {
  const [filter, setFilter] = useState("ALL");
  const [search, setSearch] = useState("");

  let filtered = filter === "ALL"
    ? transactions
    : transactions.filter(t => filter === "CREDIT" ? t.creditAmount : t.debitAmount);

  if (search) {
    const term = search.toLowerCase();
    filtered = filtered.filter(t =>
      (t.id || "").toLowerCase().includes(term) ||
      (t.description || "").toLowerCase().includes(term) ||
      (t.counterparty || "").toLowerCase().includes(term) ||
      (t.beneficiary || "").toLowerCase().includes(term) ||
      (t.mt || "").toLowerCase().includes(term)
    );
  }

  const slipFromTx = (tx) => ({
    method: tx.mt || tx.type,
    txnRef: tx.id,
    valueDate: tx.valueDate || tx.date?.slice(0, 10),
    beneficiary: tx.beneficiary || tx.counterparty || "-",
    accountNo: tx.accountNo || "-",
    receiverBank: tx.bankName || "-",
    currency: tx.currency || "USD",
    amount: tx.creditAmount || tx.debitAmount || 0,
    keyTest: tx.keyTest,
    kttEmail: tx.type === "KTT" ? "bospnktt@bankopfsaipan.com" : undefined,
    kttCode: tx.type === "KTT" ? "51 210619 BOSKTT G" : undefined,
    receiverKTTCode: tx.receiverKTTCode,
    narrative: tx.description,
  });

  const handlePrint = (tx) => setSlip(slipFromTx(tx));

  return (
    <div className="gap-16">
      <div className="card">
        <div className="card-header">
          <span className="card-title">Full Transaction Ledger</span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              className="btn btn-sm btn-gold"
              onClick={() => downloadStatementPDF(filtered, null, { title: 'Statement of Transactions' })}
              disabled={filtered.length === 0}
              title="Download statement as PDF"
            >
              Statement PDF
            </button>
            <input
              className="form-input"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: 200, padding: "6px 12px", fontSize: 13 }}
            />
            {["ALL", "CREDIT", "DEBIT"].map(f => (
              <button
                key={f}
                className={`btn btn-sm ${filter === f ? "btn-gold" : "btn-outline"}`}
                onClick={() => setFilter(f)}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div style={{ padding: 48, textAlign: "center", color: "var(--text3)" }}>
            No transactions found.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>Txn Reference</th>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Counterparty</th>
                  <th>Type</th>
                  <th>Key Test</th>
                  <th style={{ textAlign: "right" }}>Credit</th>
                  <th style={{ textAlign: "right" }}>Debit</th>
                  <th style={{ textAlign: "right" }}>Balance</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((tx, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: "var(--gold)" }}>
                      {tx.id || tx.ref}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>{formatDate(tx.date)}</td>
                    <td>{tx.description || "-"}</td>
                    <td style={{ color: "var(--text2)" }}>{tx.counterparty || tx.beneficiary || "-"}</td>
                    <td>
                      <span className={`badge ${
                        tx.mt === "API" ? "badge-api" :
                        tx.mt === "LEDGER" ? "badge-credit" :
                        "badge-ktt"
                      }`}>
                        {tx.mt || tx.type}
                      </span>
                    </td>
                    <td>
                      {tx.keyTest ? (
                        <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: "var(--navy)", fontWeight: 600 }}>
                          {tx.keyTest}
                        </span>
                      ) : "-"}
                    </td>
                    <td className="text-right" style={{ color: "var(--teal)", fontWeight: tx.creditAmount ? 600 : 400 }}>
                      {tx.creditAmount ? `+${formatUSD(tx.creditAmount)}` : "-"}
                    </td>
                    <td className="text-right" style={{ color: "var(--coral)", fontWeight: tx.debitAmount ? 600 : 400 }}>
                      {tx.debitAmount ? `-${formatUSD(tx.debitAmount)}` : "-"}
                    </td>
                    <td className="text-right" style={{ fontWeight: 600 }}>
                      {tx.balanceAfter !== undefined ? formatUSD(tx.balanceAfter) : "-"}
                    </td>
                    <td>
                      <span className={`badge badge-${tx.status === "SETTLED" ? "settled" : "pending"}`}>
                        {tx.status}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => handlePrint(tx)}
                          title="Print receipt"
                        >
                          Print
                        </button>
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => downloadSlipPDF(slipFromTx(tx))}
                          title="Download slip PDF"
                        >
                          PDF
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {filtered.length > 0 && (
          <div style={{ padding: "12px 24px", borderTop: "1px solid var(--border-light)", fontSize: 13, color: "var(--text3)", textAlign: "right" }}>
            Showing {filtered.length} of {transactions.length} transactions
          </div>
        )}
      </div>
    </div>
  );
}
